import { ConsiderationValue } from "swan-public-api-client";

export function getConsiderationSorter<T>(
  selector: (x: T) => ConsiderationValue | undefined
) {
  return (a: T, b: T) => {
    const aConsiderationValue = selector(a);
    const bConsiderationValue = selector(b);

    // check whether considerations are set. Unset considerations are sorted to the top
    if (!aConsiderationValue && !bConsiderationValue) {
      return 0;
    }
    if (aConsiderationValue && !bConsiderationValue) {
      return -1;
    }
    if (!aConsiderationValue && bConsiderationValue) {
      return 1;
    }
    // sort by abs value, and don't worry that different rows are in different currencies
    const bAbsConsideration = Math.abs(bConsiderationValue!.Value);
    const aAbsConsideration = Math.abs(aConsiderationValue!.Value);
    if (aAbsConsideration < bAbsConsideration) {
      return -1;
    }
    if (aAbsConsideration > bAbsConsideration) {
      return 1;
    }

    return 0;
  };
}

function isDefined<T>(x: T | undefined | null): boolean {
  return x !== undefined && x !== null;
};

export function getDateSorter<T>(selector: (x: T) => Date | null | undefined) {
  return getDefinedSorter(
    selector,
    (a, b) => a === b
      ? 0
      : a < b
        ? -1
        : 1
  );
}

export function getReferenceSorter<T>(selector: (x: T) => string) {
  const extractId = (reference: string) => {
    const splitReference = reference.split("-");
    const idString = splitReference[splitReference.length - 1];
    const id = parseInt(idString, 10);
    return id;
  };

  return (a: T, b: T) => {
    const aValue = selector(a);
    const aId = extractId(aValue);

    const bValue = selector(b);
    const bId = extractId(bValue);

    if (aId < bId) {
      return -1;
    }
    if (aId > bId) {
      return 1;
    }

    return 0;
  };
}

export function getNumberSorter<T>(selector: (x: T) => number | null | undefined) {
  return getDefinedSorter(
    selector,
    (a, b) => a === b
      ? 0
      : a < b
        ? -1
        : 1
  );
}

function getDefinedSorter<TRecord, TValue>(
  selector: (x: TRecord) => TValue | null | undefined,
  sorter: (a: TValue, b: TValue) => -1 | 0 | 1
) {
  return (a: TRecord, b: TRecord) => {
    const aValue = selector(a);
    const bValue = selector(b);

    if (!isDefined(aValue) && !isDefined(bValue)) {
      return 0;
    }
    if (isDefined(aValue) && !isDefined(bValue)) {
      return -1;
    }
    if (!isDefined(aValue) && isDefined(bValue)) {
      return 1;
    }

    return sorter(aValue!, bValue!);
  };
}
